import { type ReactionSummary } from '@mesa/core/reactions';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';


interface ReactionButtonProps {
  reaction: ReactionSummary;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
}

export function ReactionButton({ reaction, onClick, disabled = false, loading = false }: ReactionButtonProps) {
  const { emoji, count, userReacted } = reaction;

  // Create tooltip content with user list
  const tooltipContent = () => {
    const otherCount = count - (userReacted ? 1 : 0);
    if (otherCount === 0) return `You reacted with ${emoji}`;
    return `${otherCount} other${otherCount === 1 ? '' : 's'} reacted with ${emoji}`;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={onClick}
            disabled={disabled || loading}
            className={cn(
              'h-7 px-2 py-1 gap-1.5 text-xs font-medium transition-colors',
              'border border-gray-200 hover:border-gray-300',
              'bg-white hover:bg-gray-50',
              userReacted && 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300',
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            <span className="text-sm leading-none">{emoji}</span>
            <span className="text-xs leading-none">{count}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{tooltipContent()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
